import { useEffect, useState } from "react";
import type { AdPost } from "../../../shared/goals/ready";
import { useNative } from "../../../shared/phone/media";
import { Sheet as PnSheet } from "../../../shared/phone/Sheet";
import { Sheet } from "../../components/ui/Sheet";
import { Icon } from "../../components/ui/Icon";
import { ApiError } from "../../lib/api";
import { readyApi } from "../../lib/readyApi";
import { postDay } from "./AdItem";

/** The latest posts on the business's Facebook Page, to promote one as an ad (prototype v29, "Pick a post"). */
export function PostPicker({ goalId, productName, open, onClose, onPick }: { goalId: string; productName: string; open: boolean; onClose: () => void; onPick: (post: AdPost) => void }) {
  const [posts, setPosts] = useState<AdPost[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (!open) return;
    setError(null);
    readyApi.pagePosts(goalId).then(
      (r) => setPosts(r.posts),
      (e: unknown) => setError(e instanceof ApiError ? e.message : "Facebook did not answer. Try again."),
    );
  }, [open, goalId]);
  const native = useNative();

  if (native)
    return (
      <PnSheet open={open} onClose={onClose} tall title="Pick a post">
        <p className="ad-shs">Posts from the {productName} page. Its likes and comments come with it.</p>
        {error ? (
          <p className="pn-note ad-bad">{error}</p>
        ) : !posts ? (
          <p className="pn-empty">Loading your posts from Facebook</p>
        ) : !posts.length ? (
          <p className="pn-empty">Your Page has no posts yet.</p>
        ) : (
          <div className="pn-grp">
            {posts.map((p) => (
              <button key={p.id} type="button" className="pn-row wrap ad-adr" onClick={() => onPick(p)}>
                <span className={p.picture ? "ad-th has" : "ad-th"}>
                  {p.picture ? <img src={p.picture} alt="" loading="lazy" referrerPolicy="no-referrer" /> : <Icon name="play" />}
                </span>
                <span className="m">
                  <span className="t">Published {postDay(p.at)}</span>
                  <span className="s">{p.text || "No text"}</span>
                  <span className="s">
                    {p.reactions} reactions · {p.comments} comments
                  </span>
                </span>
              </button>
            ))}
          </div>
        )}
      </PnSheet>
    );

  return (
    <Sheet open={open} onClose={onClose} label="Pick a post" className="postpick">
      <span className="eyebrow">{productName} on Facebook</span>
      <h2>Pick a post</h2>
      <p className="hint">Your latest posts. Its likes and comments come with it.</p>
      {error ? (
        <p className="warnl">{error}</p>
      ) : !posts ? (
        <div className="posts" aria-busy="true">
          <p className="pp-load">
            <span className="spin" />
            Loading your posts from Facebook
          </p>
          {[0, 1, 2].map((i) => (
            <div key={i} className="pst ghost" aria-hidden="true">
              <span className="pst-m" />
              <span className="pst-lines">
                <i />
                <i />
              </span>
            </div>
          ))}
        </div>
      ) : !posts.length ? (
        <p className="hint">Your Page has no posts yet.</p>
      ) : (
        <div className="posts">
          {posts.map((p) => (
            <button key={p.id} type="button" className="pst pick" onClick={() => onPick(p)}>
              <span className="pst-m">
                {p.picture && <img src={p.picture} alt="" loading="lazy" referrerPolicy="no-referrer" onError={(e) => (e.currentTarget.hidden = true)} />}
                {p.kind === "Video" && <Icon name="play" />}
                <span className="pst-k">{p.kind}</span>
              </span>
              <div>
                <b>{postDay(p.at)}</b>
                <p>{p.text || "No text"}</p>
                <span className="hint">
                  {p.reactions} reactions · {p.comments} comments
                </span>
              </div>
            </button>
          ))}
        </div>
      )}
    </Sheet>
  );
}
